import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Container, Box, Paper,
  Dialog, DialogTitle, DialogContent, TextField, Button,
  IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  ShoppingCart as OrdersIcon,
  Inventory as ProductsIcon,
  Analytics as AnalyticsIcon,
  CompareArrows as CompareIcon,
  AttachMoney as PricingIcon,
  FindInPage as OrderSearchIcon,
  Settings as SettingsIcon,
  Warehouse as InventoryIcon,
  Menu as MenuIcon,
  SyncAlt as TransferIcon,
  ShoppingBasket as PurchaseIcon,
  BrandingWatermark as LogosIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import Dashboard from './components/Dashboard';
import ProductSearch from './components/ProductSearch';
import PendingOrders from './components/PendingOrders';
import PendingProducts from './components/PendingProducts';
import ProductComparison from './components/ProductComparison';
import PricingAnalysis from './components/PricingAnalysis';
import OrderSearch from './components/OrderSearch';
import Settings from './components/Settings';
import Inventory from './components/Inventory';
import Purchase from './components/Purchase';
import Logos from './components/Logos';

const navItems = [
  { label: 'Overzicht',           path: '/',           icon: <DashboardIcon /> },
  { label: 'Producten Zoeken',    path: '/search',     icon: <SearchIcon /> },
  { label: 'Openstaande Orders',  path: '/orders',     icon: <OrdersIcon /> },
  { label: 'Order Opzoeken',      path: '/order-search', icon: <OrderSearchIcon /> },
  { label: 'Open Producten',      path: '/products',   icon: <ProductsIcon /> },
  { label: 'Analytics',           path: '/comparison', icon: <CompareIcon /> },
  { label: 'Prijsanalyse',        path: '/pricing',    icon: <PricingIcon /> },
  { label: 'Instellingen',        path: '/settings',   icon: <SettingsIcon /> },
  { label: 'Voorraad',            path: '/inventory',  icon: <InventoryIcon />, highlight: true },
  { label: 'Inkoop',              path: '/purchase',   icon: <PurchaseIcon />,  highlight: false },
  { label: "Logo's",              path: '/logos',      icon: <LogosIcon />,     highlight: true },
];

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Pincode
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [unlocked, setUnlocked] = useState(() =>
    localStorage.getItem('dashboard_unlocked') === 'true'
  );

  const handlePinSubmit = () => {
    if (pin === '2021') {
      setUnlocked(true);
      localStorage.setItem('dashboard_unlocked', 'true');
      setPinError(false);
      // Bewaar huidige pad + query params (deeplink werkt automatisch)
    } else {
      setPinError(true);
    }
  };

  if (!unlocked) {
    return (
      <Dialog open fullWidth maxWidth="xs" PaperProps={{ sx: { textAlign: 'center', p: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', mb: 1 }}>Dashboard beveiligd</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Voer de pincode in om toegang te krijgen.</Typography>
          <TextField
            label="Pincode"
            type="password"
            value={pin}
            onChange={e => setPin(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handlePinSubmit()}
            error={pinError}
            helperText={pinError ? 'Onjuiste pincode' : ' '}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'new-password' }}
            sx={{ mb: 2 }}
          />
          <Button onClick={handlePinSubmit} variant="contained" fullWidth sx={{ bgcolor: '#ffcc00', color: '#000', fontWeight: 'bold', '&:hover': { bgcolor: '#e6b800' } }}>
            Ontgrendel
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  const currentPath = location.pathname;

  const handleNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const currentLabel = navItems.find(n => n.path === currentPath)?.label || 'HH Dash';

  const drawerContent = (
    <Box sx={{ width: 260 }}>
      <Box sx={{ bgcolor: '#ffcc00', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DashboardIcon sx={{ color: '#000' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>HH Dash</Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={currentPath === item.path}
              onClick={() => handleNav(item.path)}
              sx={{
                '&.Mui-selected': { bgcolor: item.highlight ? '#fff8e1' : '#f5f5f5', borderLeft: item.highlight ? '3px solid #ffcc00' : '3px solid #1976d2' },
              }}
            >
              <ListItemIcon sx={{ color: item.highlight && currentPath === item.path ? '#f57c00' : 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: currentPath === item.path ? 'bold' : 'normal', fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#ffcc00' }}>
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" sx={{ mr: 1, color: '#000' }} onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          <DashboardIcon sx={{ mr: 1.5, color: '#000' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#000', fontWeight: 'bold' }}>
            {isMobile ? currentLabel : 'HH Dash'}
          </Typography>
          {/* Snelknop Transfer op mobiel */}
          {isMobile && currentPath !== '/inventory' && (
            <IconButton sx={{ color: '#000' }} onClick={() => handleNav('/inventory?subtab=transfer')} title="Transfer">
              <TransferIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobiel: Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawerContent}
      </Drawer>

      <Container maxWidth="xl" sx={{ mt: isMobile ? 1 : 3, px: isMobile ? 1 : 3 }}>
        {/* Desktop: tabs via Paper */}
        {!isMobile && (
          <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', overflowX: 'auto' }}>
              {navItems.map((item) => (
                <Box
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    px: 2, py: 1.5, cursor: 'pointer', minWidth: 80, flex: 1,
                    borderBottom: currentPath === item.path ? '2px solid #1976d2' : '2px solid transparent',
                    color: currentPath === item.path ? '#1976d2' : 'text.secondary',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  {item.icon}
                  <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center', lineHeight: 1.2, fontWeight: currentPath === item.path ? 'bold' : 'normal' }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ p: 3 }}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/search" element={<ProductSearch />} />
                <Route path="/orders" element={<PendingOrders />} />
                <Route path="/order-search" element={<OrderSearch />} />
                <Route path="/products" element={<PendingProducts />} />
                <Route path="/comparison" element={<ProductComparison />} />
                <Route path="/pricing" element={<PricingAnalysis />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/purchase" element={<Purchase />} />
                <Route path="/logos" element={<Logos />} />
              </Routes>
            </Box>
          </Paper>
        )}

        {/* Mobiel: geen Paper wrapper */}
        {isMobile && (
          <Box sx={{ pb: 2 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/search" element={<ProductSearch />} />
              <Route path="/orders" element={<PendingOrders />} />
              <Route path="/order-search" element={<OrderSearch />} />
              <Route path="/products" element={<PendingProducts />} />
              <Route path="/comparison" element={<ProductComparison />} />
              <Route path="/pricing" element={<PricingAnalysis />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/logos" element={<Logos />} />
            </Routes>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default App;
